import React, { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useAuth } from '../hooks/useAuth';
import Logo from '../components/Logo';

declare global {
  interface Window {
    google: any;
  }
}

const LoginPage: React.FC = () => {
  const { isAuthenticated, login, loading } = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const isGoogleLoaded = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const loadGoogleScript = () => {
      if (window.google || isGoogleLoaded.current) return;
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      document.head.appendChild(script);
      isGoogleLoaded.current = true;
    };

    const initializeGoogleSignIn = () => {
      if (!window.google || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: '100%',
        text: 'signin_with',
        shape: 'rectangular',
      });
    };

    loadGoogleScript();
  }, []);

  const handleGoogleResponse = async (response: any) => {
    try {
      const success = await login(response.credential);
      if (success) {
        setTimeout(() => navigate('/app/dashboard', { replace: true }), 100);
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleMockLogin = async () => {
    const mockGoogleToken = 'mock-google-token-' + Date.now();
    try {
      const success = await login(mockGoogleToken);
      if (success) {
        setTimeout(() => navigate('/app/dashboard', { replace: true }), 100);
      }
    } catch (error) {
      console.error('Mock login error:', error);
    }
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2.5rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    maxWidth: '420px',
    width: '100%'
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#3B82F6' }}></i>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '1rem 2rem', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Logo size="md" />
        </Link>
        <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.875rem' }}>
          <i className="pi pi-arrow-left" style={{ marginRight: '0.5rem' }}></i>Back to Home
        </Link>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', padding: '2rem' }}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <i className="pi pi-sign-in" style={{ color: 'white', fontSize: '1.5rem' }}></i>
            </div>
            <h1 style={{ fontSize: '1.75rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>Welcome Back</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>Sign in to access your research projects</p>
          </div>

          {/* Info Box */}
          <div style={{ backgroundColor: '#dbeafe', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <i className="pi pi-info-circle" style={{ color: '#1e40af', marginTop: '0.125rem' }}></i>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
              Sign in with your Google account to access the research automation platform
            </p>
          </div>

          {/* Google OAuth Button */}
          <div ref={googleButtonRef} style={{ marginBottom: '1rem' }} />

          {/* Dev Mock Login */}
          {import.meta.env.VITE_ENABLE_MOCK_LOGIN === 'true' && (
            <>
              <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Development Mode</span>
              </div>
              <Button
                label="Mock Login (Dev Only)"
                icon="pi pi-user"
                className="p-button-outlined w-full"
                onClick={handleMockLogin}
                loading={loading}
              />
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
              <i className="pi pi-lock" style={{ marginRight: '0.5rem' }}></i>
              Secure authentication powered by Google OAuth
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '1rem 2rem', backgroundColor: 'white', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
          © 2024 Insights Research. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
